/**
 * Lite session — cookie-backed waitlist token (T11 §3.4).
 *
 * Waitlist users do not have Cerberus identity (no password / JWT). Peitho
 * issues a `lite_session_token` (ULID) on signup so the user can revisit
 * the waitlist surface and read their own position + invites without ever
 * authenticating. This module owns reading / writing the cookie that
 * carries the token between visits.
 *
 * The cookie is namespaced (`cuidari_waitlist_token`), `Secure`,
 * `SameSite=Lax`, scoped to `path=/`, and TTL is 1 year. We intentionally
 * do NOT mark `HttpOnly` here because cookie writes happen from the
 * browser (post-signup response or `?t=<token>` magic-link query) — the
 * server cannot write it on these flows. The risk is bounded: the token
 * only authorises read of waitlist position; no mutation API accepts it.
 *
 * Boundary: this is NOT identity (Cerberus). Lite session = pre-auth
 * surface read-only, per the boundary canonicalised in
 * `docs/specs/cuidari/t11-growth-loops.md §3.4`.
 */

const COOKIE_NAME = 'cuidari_waitlist_token'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 // 1 year

function isBrowser(): boolean {
  return typeof document !== 'undefined' && typeof document.cookie === 'string'
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null
  const target = `${name}=`
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length))
    }
  }
  return null
}

function isSecureContext(): boolean {
  if (typeof window === 'undefined') return false
  // Production traffic is HTTPS so we always tag the cookie `Secure`.
  // jsdom test runs land on `http://localhost` and the cookie store
  // rejects `Secure` cookies on insecure origins — making the flag
  // unconditional would collapse the cookie round-trip in tests.
  return window.location?.protocol === 'https:'
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (!isBrowser()) return
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    'Path=/',
    'SameSite=Lax',
  ]
  if (isSecureContext()) parts.push('Secure')
  document.cookie = parts.join('; ')
}

function deleteCookie(name: string): void {
  if (!isBrowser()) return
  const parts = [`${name}=`, 'Max-Age=0', 'Path=/', 'SameSite=Lax']
  if (isSecureContext()) parts.push('Secure')
  document.cookie = parts.join('; ')
}

export const liteSession = {
  /** Returns the current lite session token, or null if absent. */
  getToken(): string | null {
    return readCookie(COOKIE_NAME)
  },
  /** Persists `token` in the lite session cookie (1y TTL). */
  setToken(token: string): void {
    writeCookie(COOKIE_NAME, token, COOKIE_MAX_AGE_SECONDS)
  },
  /** Removes the lite session cookie. Idempotent. */
  clearToken(): void {
    deleteCookie(COOKIE_NAME)
  },
  /** Internal: cookie name; exported for tests + smoke evidence. */
  COOKIE_NAME,
}
